import React, { useState, useEffect } from "react";
import axios from "axios";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import useDoctors from "../../../hook/useDoctors";
import PageHeading from "../../PageHeading";
import CreatableSelect from "react-select/creatable";
import { toast, ToastContainer } from "react-toastify";
import DynamicSelect from "../../DynamicSelect";

// Helper function: format date as "YYYY-MM-DD"
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "Invalid Date";
  return date.toLocaleDateString("en-US");
};

const getAvailableDatesForMonth = (selectedMonth, availableDays) => {
  const year = selectedMonth.getFullYear();
  const month = selectedMonth.getMonth();
  const totalDays = new Date(year, month + 1, 0).getDate();
  const availableDates = [];
  for (let day = 1; day <= totalDays; day++) {
    const date = new Date(year, month, day);
    if (availableDays.includes(date.getDay())) {
      availableDates.push(date);
    }
  }
  return availableDates;
};

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const BASE_URL = "https://api.muktihospital.com/api";

const fetchData = async (endpoint) => {
  try {
    const response = await axios.get(`${BASE_URL}${endpoint}`, {
      headers: { "x-api-key": API_KEY },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching data:", error);
    return null;
  }
};

const AddAppointment = () => {
  // State declarations
  const [loading, setLoading] = useState(false);
  const [patientLoading, setPatientLoading] = useState(true);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [appointmentData, setAppointmentData] = useState([]);
  const { doctors, loading: doctorLoading, fetchDoctors } = useDoctors();
  const [patients, setPatients] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState({
    value: "CASH",
    label: "Cash"
  });
  const [selectedFee, setSelectedFee] = useState(null);
  const [feeOptions, setFeeOptions] = useState([]);
  const [selectedBloodGroup, setSelectedBloodGroup] = useState(null);

  // Form data state
  const [formData, setFormData] = useState({
    doctor: "",
    patient: "",
    consultationFee: "",
    paymentMethod: "CASH",
    reference: "",
    mobileNumber: "",
    weight: "",
    age: "",
    serialNumber: "",
    reason: "",
    address: "",
    bloodGroup: "",
    isManualPatient: false,
  });

  // Initial form state for resets
  const initialFormData = {
    doctor: "",
    patient: "",
    consultationFee: "",
    paymentMethod: "",
    reference: "",
    mobileNumber: "",
    weight: "",
    age: "",
    serialNumber: "",
    reason: "",
    address: "",
    bloodGroup: "",
    isManualPatient: false,
  };

  // Fetch doctors on mount
  useEffect(() => {
    fetchDoctors();
  }, [fetchDoctors]);

  // Fetch patients on mount
  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/patient`, {
          headers: { "x-api-key": API_KEY },
        });
        const formattedPatients = response.data.map((patient) => ({
          value: patient.id,
          label: `${patient.name} (${patient.phoneNumber || "No Phone"})`,
          phone: patient.phoneNumber || "",
        }));
        setPatients(formattedPatients);
      } catch (error) {
        console.error("Error loading patients:", error);
      } finally {
        setPatientLoading(false);
      }
    };
    fetchPatients();
  }, []);

  // Fetch existing appointments on mount
  useEffect(() => {
    const loadAppointments = async () => {
      const appointmentsRes = await fetchData("/appointment");
      if (appointmentsRes && appointmentsRes.appointments) {
        setAppointmentData(appointmentsRes.appointments);
      } else if (Array.isArray(appointmentsRes)) {
        setAppointmentData(appointmentsRes);
      } else {
        setAppointmentData([]);
      }
    };
    loadAppointments();
  }, []);

  // Handle doctor selection
  const handleDoctorChange = (selectedOption) => {
    setSelectedDoctor(selectedOption);
    setFormData({ ...formData, doctor: selectedOption?.value || "" });

    if (!selectedOption) {
      setAvailableDates([]);
      setFeeOptions([]);
      setSelectedFee(null);
      return;
    }

    setScheduleLoading(true);
    const doctor = doctors.find((doc) => doc.id === selectedOption.value);
    if (doctor) {
      const daysMapping = {
        Sunday: 0,
        Monday: 1,
        Tuesday: 2,
        Wednesday: 3,
        Thursday: 4,
        Friday: 5,
        Saturday: 6,
      };
      const availableDays =
        doctor?.schedule?.map((sch) => daysMapping[sch.day]) || [];
      setAvailableDates(getAvailableDatesForMonth(selectedDate, availableDays));

      const appointmentFee = doctor.translations?.en?.appointmentFee;
      const followUpFee = doctor.translations?.en?.followUpFee;
      setFeeOptions([
        { value: appointmentFee, label: `Appointment Fee (${appointmentFee})` },
        { value: followUpFee, label: `Follow-Up Fee (${followUpFee})` },
      ]);
      setSelectedFee(null);
    }
    setScheduleLoading(false);
  };

  // Handle date change
  const handleDateChange = (date) => {
 
    const localDate = new Date(date);
    localDate.setHours(12, 0, 0, 0);
    setSelectedDate(localDate);
  };

  // Handle fee change
  const handleFeeChange = (selectedOption) => {
    if (selectedOption) {
      setSelectedFee({ value: selectedOption.value, label: selectedOption.value });
      setFormData({ ...formData, consultationFee: selectedOption.value });
    } else {
      setSelectedFee(null);
      setFormData({ ...formData, consultationFee: "" });
    }
  };

  // Handle patient selection
  const handlePatientChange = (selectedOption) => {
    setSelectedPatient(selectedOption);
    setFormData({
      ...formData,
      patient: selectedOption?.value || "",
      mobileNumber: selectedOption?.phone || "",
      isManualPatient: selectedOption?.value?.startsWith("new-") || false,
    });
  };

  // Handle new patient creation
  const handleCreatePatient = (inputValue) => {
    const trimmedInput = inputValue.trim();
    if (!trimmedInput) {
      toast.warn("Patient name cannot be empty!");
      return;
    }

    // Check for duplicate patient names (case-insensitive)
    const isDuplicate = patients.some(
      (patient) => patient.label.toLowerCase() === trimmedInput.toLowerCase()
    );
    if (isDuplicate) {
      toast.warn("Patient with this name already exists!");
      return;
    }

    // Create new patient
    const newPatient = {
      value: `new-${Date.now()}`,
      label: trimmedInput,
      phone: "",
    };

    // Update patients list and select the new patient
    setPatients((prev) => [...prev, newPatient]);
    setSelectedPatient(newPatient);
    setFormData({
      ...formData,
      patient: newPatient.value,
      mobileNumber: "",
      isManualPatient: true,
    });
  };

  // Handle payment method change
  const handlePaymentMethodChange = (selectedOption) => {
    setSelectedPaymentMethod(selectedOption);
    setFormData({ ...formData, paymentMethod: selectedOption?.value || "" });
  };

  // Handle blood group change
  const handleBloodGroupChange = (selectedOption) => {
    setSelectedBloodGroup(selectedOption);
    setFormData({ ...formData, bloodGroup: selectedOption?.value || null });
  };

  // Generic change handler for other fields
  const handleChangeFields = (e) => {
    const { name, value } = e.target;
    if (name === "mobileNumber") {
      let cleanedValue = value.trim();
      if (cleanedValue.startsWith("88")) {
        cleanedValue = cleanedValue.substring(2);
      }
      setFormData({ ...formData, [name]: cleanedValue });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // Auto-generate Serial Number
  useEffect(() => {
    if (!selectedDoctor || !selectedDate) return;
    const currentAppointments = Array.isArray(appointmentData) ? appointmentData : [];
    const formattedSelectedDate = formatDate(selectedDate);
    const matchingAppointments = currentAppointments.filter((app) => {
      return (
        app.doctorId === selectedDoctor.value &&
        formatDate(new Date(app.appointmentDate)) === formattedSelectedDate
      );
    });
    const newSerial = matchingAppointments.length + 1;
    setFormData((prev) => ({ ...prev, serialNumber: newSerial }));
  }, [selectedDoctor, selectedDate, appointmentData]);

  // Reset form function
  const resetForm = () => {
    setFormData(initialFormData);
    setSelectedDoctor(null);
    setSelectedPatient(null);
    setSelectedPaymentMethod({ value: "CASH", label: "Cash" });
    setSelectedFee(null);
    setSelectedBloodGroup(null);
    setSelectedDate(new Date());
    setAvailableDates([]);
    setFeeOptions([]);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formattedDate = `${selectedDate.getFullYear()}-${String(
      selectedDate.getMonth() + 1
    ).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`;

    let cleanedMobileNumber = formData.mobileNumber.trim();
    if (cleanedMobileNumber && !cleanedMobileNumber.startsWith("88")) {
      cleanedMobileNumber = "88" + cleanedMobileNumber;
    }

    const payload = {
      doctorId: selectedDoctor ? selectedDoctor.value : "",
      doctorName: selectedDoctor ? selectedDoctor.label : "",
      patientId:
        selectedPatient && !selectedPatient.value.startsWith("new-")
          ? selectedPatient.value
          : null,
      patientName: selectedPatient ? selectedPatient.label : "",
      appointmentDate: formattedDate,
      consultationFee: parseFloat(formData.consultationFee) || 0,
      paymentMethod: formData.paymentMethod,
      serialNumber: formData.serialNumber,
      mobileNumber: cleanedMobileNumber || null,
      weight: formData.weight ? parseFloat(formData.weight) : null,
      age: formData.age ? parseInt(formData.age) : null,
      reference: formData.reference || null,
      bloodGroup: formData.bloodGroup || null, // Should be A_POSITIVE, etc.
      reason: formData.reason || null,
      address: formData.address || null,
      isNewPatient:
        selectedPatient && selectedPatient.value.startsWith("new-") ? true : false,
    };

    // Log payload for debugging
    // console.log("Submitting payload:", payload);

    try {
      const response = await axios.post(`${BASE_URL}/appointment/add`, payload, {
        headers: { "x-api-key": API_KEY },
      });
      toast.success(response.data.message || "Appointment booked successfully!");
      resetForm();
    } catch (error) {
      console.error("Error booking appointment:", error.response?.data || error.message);
      toast.error(error.response?.data?.message || "Failed to book appointment.");
    } finally {
      setLoading(false);
    }
  };
  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Manage Appointments"},
    { label: "Add New Appointment" },
  ];

  // Update available dates for the calendar
  useEffect(() => {
    if (!selectedDoctor) return;
    const doctor = doctors.find((doc) => doc.id === selectedDoctor.value);
    if (!doctor) return;
    const daysMapping = {
      Sunday: 0,
      Monday: 1,
      Tuesday: 2,
      Wednesday: 3,
      Thursday: 4,
      Friday: 5,
      Saturday: 6,
    };
    const availableDays = doctor?.schedule?.map((sch) => daysMapping[sch.day]) || [];
    const calendarStartDate = new Date(selectedDate);
    calendarStartDate.setDate(1);
    const dayOfWeek = calendarStartDate.getDay();
    calendarStartDate.setDate(calendarStartDate.getDate() - dayOfWeek);
    const fullVisibleDates = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(calendarStartDate);
      d.setDate(calendarStartDate.getDate() + i);
      if (availableDays.includes(d.getDay())) {
        fullVisibleDates.push(new Date(d));
      }
    }
    setAvailableDates(fullVisibleDates);
  }, [selectedDoctor, selectedDate, doctors]);

  return (
    <div>
      <ToastContainer />
      <PageHeading title="Add New Appointment" breadcrumbs={breadcrumbs} />
      <div className="">
        <form onSubmit={handleSubmit}>
          <div className="bg-white shadow-sm rounded-lg">
            <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
              <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                Basic Information
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5">
              {/* Doctor Selection */}
              <div>
                <label className="label">Select Doctor</label>
                {doctorLoading ? (
                  <Skeleton height={40} />
                ) : (
                  <DynamicSelect
                    options={doctors.map((doc) => ({
                      value: doc.id,
                      label: doc.name || (doc.translations?.en?.name ?? "Unknown Doctor"),
                    }))}
                    value={selectedDoctor}
                    className="input-field"
                    onChange={handleDoctorChange}
                    placeholder="Choose a Doctor"
                    isClearable
                  />
                )}
              </div>

              {/* Patient Selection */}
              <div>
                <label className="label">Select Patient</label>
                {patientLoading ? (
                  <Skeleton height={40} />
                ) : (
                  <CreatableSelect
                    options={patients}
                    value={selectedPatient}
                    onChange={handlePatientChange}
                    onCreateOption={handleCreatePatient}
                    placeholder="Type a name and press Enter to add new patient"
                    isClearable
                    isSearchable
                    formatCreateLabel={(inputValue) => `Add "${inputValue}" as new patient`}
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        borderColor: '#e2e8f0',
                        fontSize: '14px',
                        backgroundColor: '#f7fafc',
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        fontSize: '14px',
                        backgroundColor: state.isSelected
                          ? '#3b82f6'
                          : state.isFocused
                          ? '#ebf8ff'
                          : 'transparent',
                        color: state.isSelected ? '#ffffff' : '#2d3748',
                      }),
                      menu: (provided) => ({
                        ...provided,
                        backgroundColor: '#ffffff',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        padding: '0px',
                      }),
                    }}
                  />
                )}
              </div>

              {/* Payment Method */}
              <div>
                <label className="label">Payment Method</label>
                <DynamicSelect
                  options={[
                    { value: "CASH", label: "Cash" },
                    { value: "BKASH", label: "Bkash" },
                    { value: "NAGAD", label: "Nagad" },
                    { value: "ROCKET", label: "Rocket" },
                    { value: "BANK", label: "Bank" },
                  ]}
                  value={selectedPaymentMethod}
                  onChange={handlePaymentMethodChange}
                  placeholder="Choose Payment Method"
                  isClearable
                />
              </div>

              {/* Fee Selection */}
              <div>
                <label className="label">Fee</label>
                <DynamicSelect
                  options={feeOptions}
                  value={selectedFee}
                  onChange={handleFeeChange}
                  placeholder="Select Fee"
                  isClearable
                />
              </div>

              {/* Serial Number - Auto-generated */}
              <div>
                <label className="label">Serial Number</label>
                <input
                  type="number"
                  name="serialNumber"
                  className="input-field"
                  value={formData.serialNumber || ""}
                  onChange={handleChangeFields}
                  placeholder="Auto-generated Serial Number"
                  readOnly
                />
              </div>

              {/* Mobile Number */}
              <div>
                <label className="label">Mobile Number</label>
                <input
                  type="number"
                  name="mobileNumber"
                  className="input-field"
                  value={formData.mobileNumber}
                  onChange={handleChangeFields}
                  disabled={formData.isManualPatient ? false : !!selectedPatient}
                />
              </div>

              {/* Blood Group */}
              <div>
                <label className="label">Blood Group</label>
                <DynamicSelect
                  options={[
                    { value: "A_POSITIVE", label: "A+" },
                    { value: "A_NEGATIVE", label: "A-" },
                    { value: "B_POSITIVE", label: "B+" },
                    { value: "B_NEGATIVE", label: "B-" },
                    { value: "O_POSITIVE", label: "O+" },
                    { value: "O_NEGATIVE", label: "O-" },
                    { value: "AB_POSITIVE", label: "AB+" },
                    { value: "AB_NEGATIVE", label: "AB-" },
                  ]}
                  value={selectedBloodGroup}
                  onChange={handleBloodGroupChange}
                  placeholder="Select Blood Group"
                  isClearable
                />
              </div>

              {/* Weight Field */}
              <div>
                <label className="label">Weight (kg)</label>
                <input
                  type="number"
                  name="weight"
                  className="input-field"
                  value={formData.weight}
                  onChange={handleChangeFields}
                  placeholder="Enter weight in kg"
                />
              </div>

              {/* Age Field */}
              <div>
                <label className="label">Age (years)</label>
                <input
                  type="number"
                  name="age"
                  className="input-field"
                  value={formData.age}
                  onChange={handleChangeFields}
                  placeholder="Enter age in years"
                />
              </div>

              {/* Reference Field */}
              <div>
                <label className="label">Reference</label>
                <input
                  type="text"
                  name="reference"
                  className="input-field"
                  value={formData.reference}
                  onChange={handleChangeFields}
                />
              </div>

              {/* Reason */}
              <div>
                <label className="label">Reason</label>
                <textarea
                  name="reason"
                  className="input-field"
                  value={formData.reason || ""}
                  onChange={handleChangeFields}
                  onKeyDown={(e) => {
                    if (e.key === " ") e.stopPropagation();
                  }}
                  placeholder="Enter the reason for the appointment"
                />
              </div>

              {/* Address */}
              <div>
                <label className="label">Address</label>
                <input
                  type="text"
                  name="address"
                  className="input-field"
                  value={formData.address}
                  onChange={handleChangeFields}
                />
              </div>

              {/* Calendar for Appointment solución */}
              <div className="calender">
                {selectedDoctor && (
                  <div>
                    <label className="label">Select Appointment Date</label>
                    <Calendar
                      onChange={(date) => setSelectedDate(date)}
                      onActiveStartDateChange={({ activeStartDate }) => {
                        const daysMapping = {
                          Sunday: 0,
                          Monday: 1,
                          Tuesday: 2,
                          Wednesday: 3,
                          Thursday: 4,
                          Friday: 5,
                          Saturday: 6,
                        };
                        const availableDays =
                          (selectedDoctor &&
                            doctors.find((doc) => doc.id === selectedDoctor.value)
                              ?.schedule) ||
                          [];
                        const daysNum = availableDays.map((sch) => daysMapping[sch.day]);
                        const fullVisibleDates = [];
                        const calendarStartDate = new Date(activeStartDate);
                        calendarStartDate.setDate(1);
                        const dayOfWeek = calendarStartDate.getDay();
                        calendarStartDate.setDate(calendarStartDate.getDate() - dayOfWeek);
                        for (let i = 0; i < 42; i++) {
                          const d = new Date(calendarStartDate);
                          d.setDate(calendarStartDate.getDate() + i);
                          if (daysNum.includes(d.getDay())) {
                            fullVisibleDates.push(new Date(d));
                          }
                        }
                        setAvailableDates(fullVisibleDates);
                      }}
                      value={selectedDate}
                      minDate={new Date()}
                      tileDisabled={({ date }) =>
                        !availableDates.some(
                          (d) => d.toDateString() === date.toDateString()
                        )
                      }
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Submit & Reset Buttons */}
          <div className="mt-6 flex gap-4">
            <button
              type="submit"
              className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
              disabled={loading}
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
            <button
              type="reset"
              className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
              onClick={resetForm}
            >
              Clear
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAppointment;

